import React from 'react';
import { MSAService } from '@/types';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import ServiceCard from '@/components/services/management/superadmin/ServiceCard';
import { getServiceStatusBadge, getHealthBadge } from '@/utils/badgeUtils';
import { HealthStatus } from '@/types/health';

interface ServiceGridViewProps {
  services: MSAService[];
  healthStatuses: Record<number, HealthStatus>;
  loading: boolean;
  activeTab: string;
  isReadOnly?: boolean;
  onApproveClick: (serviceId: number) => void;
  onReject: (id: number, name: string) => void;
  onSuspend: (id: number, name: string) => void;
  onResume: (id: number, name: string) => void;
  onDelete: (id: number, name: string) => void;
  onRestore?: (id: number, name: string) => void;
  onCancelRejection?: (id: number, name: string) => void;
  onToggleVisibility: (id: number, name: string, newVisibility: boolean) => void;
  onServiceClick?: (service: MSAService) => void;
  onManagePlans?: (service: MSAService) => void;
  onManageClient?: (service: MSAService) => void;
}

export const ServiceGridView: React.FC<ServiceGridViewProps> = ({
  services,
  healthStatuses,
  loading,
  activeTab,
  isReadOnly = false,
  onApproveClick,
  onReject,
  onSuspend,
  onResume,
  onDelete,
  onRestore,
  onCancelRejection,
  onToggleVisibility,
  onServiceClick,
  onManagePlans,
  onManageClient,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <LoadingSpinner size="md" message="서비스를 불러오는 중..." />
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">
          {activeTab === 'pending' && '등록 대기 중인 서비스가 없습니다.'}
          {activeTab === 'approved' && '등록된 서비스가 없습니다.'}
          {activeTab === 'all' && '등록된 서비스가 없습니다.'}
          {activeTab === 'deleted_rejected' && '삭제/거부된 서비스가 없습니다.'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {services.map((service) => (
        <ServiceCard
          key={service.id}
          service={service}
          healthStatus={healthStatuses[service.id]}
          isReadOnly={isReadOnly}
          onApprove={onApproveClick}
          onReject={onReject}
          onSuspend={onSuspend}
          onResume={onResume}
          onDelete={onDelete}
          onRestore={onRestore}
          onCancelRejection={onCancelRejection}
          onToggleVisibility={onToggleVisibility}
          onServiceClick={onServiceClick}
          onManagePlans={onManagePlans}
          onManageClient={onManageClient}
          getStatusBadge={getServiceStatusBadge}
          getHealthBadge={getHealthBadge}
        />
      ))}
    </div>
  );
};

